/**
 * Error Messages for Auth Operations
 * Centralized error code mappings for Better Auth organization/team/member operations
 * All messages are in Portuguese for user-facing errors
 */

export interface ErrorMessageConfig {
  title: string;
  description?: string;
}

export const AUTH_ERROR_MESSAGES: Record<string, ErrorMessageConfig> = {
  // Invitation errors
  INVITATION_ALREADY_EXISTS: {
    title: "Convite já existe",
    description: "Este email já possui um convite pendente para esta organização.",
  },
  DUPLICATE_INVITATION: {
    title: "Convite duplicado",
    description: "Este email já possui um convite pendente.",
  },
  INVITATION_NOT_FOUND: {
    title: "Convite não encontrado",
    description: "O convite não foi encontrado ou já foi processado.",
  },
  INVITATION_EXPIRED: {
    title: "Convite expirado",
    description: "Este convite expirou. Um novo convite precisa ser enviado.",
  },
  INVITATION_ALREADY_ACCEPTED: {
    title: "Convite já aceito",
    description: "Este convite já foi aceito anteriormente.",
  },
  INVITATION_ALREADY_CANCELED: {
    title: "Convite já cancelado",
    description: "Este convite já foi cancelado.",
  },

  // Email errors
  INVALID_EMAIL: {
    title: "Email inválido",
    description: "Verifique o formato do email e tente novamente.",
  },
  EMAIL_REQUIRED: {
    title: "Email obrigatório",
    description: "É necessário informar um email válido.",
  },

  // Permission errors
  FORBIDDEN: {
    title: "Permissão negada",
    description: "Você não tem permissão para realizar esta ação.",
  },
  UNAUTHORIZED: {
    title: "Não autorizado",
    description: "Você precisa estar autenticado para realizar esta ação.",
  },
  INSUFFICIENT_PERMISSIONS: {
    title: "Permissões insuficientes",
    description: "Você não tem permissão suficiente para realizar esta ação.",
  },

  // Member errors
  MEMBER_NOT_FOUND: {
    title: "Membro não encontrado",
    description: "O membro não foi encontrado nesta organização.",
  },
  USER_NOT_FOUND: {
    title: "Usuário não encontrado",
    description: "O usuário não foi encontrado.",
  },
  MEMBER_ALREADY_EXISTS: {
    title: "Membro já existe",
    description: "Este usuário já é membro da organização.",
  },
  CANNOT_REMOVE_LAST_OWNER: {
    title: "Não é possível remover o último proprietário",
    description: "Não é possível remover o último proprietário da organização.",
  },
  CANNOT_REMOVE_LAST_ADMIN: {
    title: "Não é possível remover o último administrador",
    description: "Não é possível remover o último administrador da organização.",
  },
  CANNOT_REMOVE_SELF: {
    title: "Não é possível remover a si mesmo",
    description: "Você não pode remover a si mesmo da organização.",
  },

  // Team errors
  TEAM_NOT_FOUND: {
    title: "Equipe não encontrada",
    description: "A equipe não foi encontrada.",
  },
  TEAM_LIMIT_EXCEEDED: {
    title: "Limite de equipes atingido",
    description: "O limite máximo de equipes para esta organização foi atingido.",
  },
  MEMBER_LIMIT_EXCEEDED: {
    title: "Limite de membros atingido",
    description: "O limite máximo de membros por equipe foi atingido.",
  },
  MEMBER_ALREADY_IN_TEAM: {
    title: "Membro já está na equipe",
    description: "Este usuário já é membro desta equipe.",
  },
  MEMBER_NOT_IN_TEAM: {
    title: "Membro não está na equipe",
    description: "Este usuário não é membro desta equipe.",
  },
  CANNOT_REMOVE_ALL_TEAMS: {
    title: "Não é possível remover todas as equipes",
    description: "É necessário manter pelo menos uma equipe na organização.",
  },

  // Organization errors
  ORGANIZATION_NOT_FOUND: {
    title: "Organização não encontrada",
    description: "A organização não foi encontrada.",
  },
  MEMBERSHIP_LIMIT_EXCEEDED: {
    title: "Limite de membros atingido",
    description: "O limite máximo de membros para esta organização foi atingido.",
  },
  BASE_PLAN_USER_LIMIT: {
    title: "Limite de usuários do plano base",
    description:
      "O plano base inclui até 3 usuários. Ative Equipes e Multi-Usuário em Assinatura para adicionar mais.",
  },

  // Validation errors
  VALIDATION_ERROR: {
    title: "Erro de validação",
    description: "Os dados fornecidos são inválidos. Verifique e tente novamente.",
  },
  INVALID_ROLE: {
    title: "Papel inválido",
    description: "O papel especificado é inválido.",
  },

  // Generic errors
  BAD_REQUEST: {
    title: "Requisição inválida",
    description: "A requisição contém dados inválidos.",
  },
  INTERNAL_ERROR: {
    title: "Erro interno",
    description: "Ocorreu um erro interno. Tente novamente mais tarde.",
  },
  NETWORK_ERROR: {
    title: "Erro de conexão",
    description: "Não foi possível conectar ao servidor. Verifique sua conexão.",
  },
} as const;

/**
 * Get user-friendly error message for a given error code
 */
export function getErrorMessage(
  errorCode: string | undefined | null
): ErrorMessageConfig | null {
  if (!errorCode) return null;

  // Try exact match first
  if (errorCode in AUTH_ERROR_MESSAGES) {
    return AUTH_ERROR_MESSAGES[errorCode];
  }

  // Try case-insensitive match
  const upperCode = errorCode.toUpperCase();
  for (const [key, value] of Object.entries(AUTH_ERROR_MESSAGES)) {
    if (key.toUpperCase() === upperCode) {
      return value;
    }
  }

  return null;
}

/**
 * Check if error message contains certain keywords and map to error codes
 */
export function inferErrorCodeFromMessage(message: string): string | null {
  const lowerMessage = message.toLowerCase();

  // Invitation errors
  if (
    lowerMessage.includes("invitation") &&
    (lowerMessage.includes("already") || lowerMessage.includes("duplicate"))
  ) {
    return "INVITATION_ALREADY_EXISTS";
  }
  if (
    lowerMessage.includes("invitation") &&
    (lowerMessage.includes("not found") || lowerMessage.includes("not exist"))
  ) {
    return "INVITATION_NOT_FOUND";
  }
  if (lowerMessage.includes("invitation") && lowerMessage.includes("expired")) {
    return "INVITATION_EXPIRED";
  }
  if (
    lowerMessage.includes("invitation") &&
    lowerMessage.includes("accepted")
  ) {
    return "INVITATION_ALREADY_ACCEPTED";
  }

  // Email errors
  if (lowerMessage.includes("invalid email") || lowerMessage.includes("email invalid")) {
    return "INVALID_EMAIL";
  }

  if (
    lowerMessage.includes("plano base inclui") ||
    lowerMessage.includes("base_plan_user_limit") ||
    (lowerMessage.includes("equipes e multi-usuário") &&
      lowerMessage.includes("usuário"))
  ) {
    return "BASE_PLAN_USER_LIMIT";
  }

  // Permission errors
  if (
    lowerMessage.includes("permission") ||
    lowerMessage.includes("forbidden") ||
    lowerMessage.includes("not authorized")
  ) {
    return "FORBIDDEN";
  }
  if (lowerMessage.includes("unauthorized")) {
    return "UNAUTHORIZED";
  }

  // Member errors
  if (
    lowerMessage.includes("member") &&
    (lowerMessage.includes("not found") || lowerMessage.includes("does not exist"))
  ) {
    return "MEMBER_NOT_FOUND";
  }
  if (
    lowerMessage.includes("member") &&
    (lowerMessage.includes("already") || lowerMessage.includes("duplicate"))
  ) {
    return "MEMBER_ALREADY_EXISTS";
  }
  if (
    lowerMessage.includes("last owner") ||
    lowerMessage.includes("cannot remove owner")
  ) {
    return "CANNOT_REMOVE_LAST_OWNER";
  }
  if (
    lowerMessage.includes("last admin") ||
    lowerMessage.includes("cannot remove admin")
  ) {
    return "CANNOT_REMOVE_LAST_ADMIN";
  }

  // Team errors
  if (
    lowerMessage.includes("team") &&
    (lowerMessage.includes("not found") || lowerMessage.includes("does not exist"))
  ) {
    return "TEAM_NOT_FOUND";
  }
  if (lowerMessage.includes("team limit") || lowerMessage.includes("too many teams")) {
    return "TEAM_LIMIT_EXCEEDED";
  }
  if (
    lowerMessage.includes("member limit") ||
    lowerMessage.includes("too many members")
  ) {
    return "MEMBER_LIMIT_EXCEEDED";
  }
  if (
    lowerMessage.includes("already in team") ||
    lowerMessage.includes("already a member of team")
  ) {
    return "MEMBER_ALREADY_IN_TEAM";
  }
  if (
    lowerMessage.includes("not in team") ||
    lowerMessage.includes("not a member of team")
  ) {
    return "MEMBER_NOT_IN_TEAM";
  }

  return null;
}

